import { useMemo } from 'react'
import moment from '@e3dc-react/shell/Libs/moment'
import { usePocData } from 'src/Libs/system-charts/shared/hooks/use-poc-data'
import { AI360ChartData } from '../types'
import { useAI360PowerPrice } from './use-ai360-power-price'
import { getSolarAndForecastData } from '../utils'
import { useAI360DynamicEmsData } from './use-ai360-dynamic-ems-data'
import { isBefore, startOfDay } from '@e3dc-react/shell/Libs/date'
import { useAI360FeedInPrices } from './use-ai360-feed-in-prices'

interface Props {
  installationId: string | undefined
  storageId: string | undefined
  /* The time range for which to fetch pv production data */
  from: Date
  to: Date
}

/**
 * Hook to fetch and combine all data needed for the AI360 line chart: power price, solar production, solar forecast, and dynamic EMS data.
 */
export const useAI360ChartData = ({ installationId, storageId, from, to }: Props): AI360ChartData[] => {
  const fromMoment = useMemo(() => moment(from), [from])
  const toMoment = useMemo(() => moment(to), [to])

  const powerPriceData = useAI360PowerPrice({
    installationId,
    from,
    to,
  })

  const feedInPriceData = useAI360FeedInPrices({
    installationId,
    from,
    to,
  })

  const { data: performanceData } = usePocData(fromMoment, toMoment, storageId ?? '', 'day')

  const dynamicEmsData = useAI360DynamicEmsData({
    installationId,
    from,
    to,
  })

  const lineChartData: AI360ChartData[] = useMemo(() => {
    const currentDate = startOfDay(new Date())
    const isDateInPast = isBefore(from, currentDate)

    return powerPriceData.map((powerPriceItem, index) => {
      const { time, price: powerPrice } = powerPriceItem
      const { price: feedInPrice } = feedInPriceData[index] || { price: null }
      const { emsBatteryMode, emsSgReadyMode } = dynamicEmsData[index] || { emsBatteryMode: null, emsSgReadyMode: null }
      const { solarForecast, solarProduction } = performanceData
        ? getSolarAndForecastData(performanceData[index], isDateInPast)
        : { solarForecast: null, solarProduction: null }

      return {
        time,
        powerPrice,
        feedInPrice,
        solarProduction,
        solarForecast,
        emsBatteryMode,
        emsSgReadyMode,
      }
    })
  }, [powerPriceData, performanceData, dynamicEmsData, from, feedInPriceData])

  return lineChartData
}
